import { Injectable } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { BehaviorSubject, map } from "rxjs";
import { SessionFacility } from "../interfaces/facility-with-timezone";
import { environment } from "../../../../environment";
import * as CryptoJS from "crypto-js";
import { MatDialog } from "@angular/material/dialog";
import { SsoToken } from "src/app/services/models/response-models/sso-token.response-model";
import { User } from "../interfaces/user.interface";
import { UserClient } from "src/app/common/services/user.client";
import { TranscribeToken } from "../interfaces/transcribe.interface";
import { AllDisciplines } from "../enums/discipline.enum";
import { BiDashboardData } from "../interfaces/Bi-Dashboard/bi-dashboard.interface";
import { BiDashboardConstants } from "../constants/bi-dashboard.constants";
import { CERTIFICATIONS_LIST_SESSION_KEYS } from "../constants/certification-constants";

export const UserSession = {
  transcribeTokenData: "transcribeTokenData",
};

@Injectable({ providedIn: "root" })
export class StorageService {
  TOKEN = "access_token";
  REFRESH_TOKEN = "refresh_token";
  loggedIn: BehaviorSubject<boolean> = new BehaviorSubject(false);
  isSelectedFacility: BehaviorSubject<string> = new BehaviorSubject("");
  isUserPhysician: BehaviorSubject<boolean> = new BehaviorSubject(false);
  shouldReloadFacilitiesDropDown: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  shouldReloadFacilitiesDropDownForUser: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  shouldReloadCorporationDropDown: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  isLogged$ = this.loggedIn.pipe(map((data) => !!data));
  lastActiveUrl = "last-active-url";
  lastActiveUserId = "last-active-user-id";
  private securityGroupChangedSubject = new BehaviorSubject<boolean>(false);
  securityGroupChanged$ = this.securityGroupChangedSubject.asObservable();
  sessionStateKeys : string[] = [this.lastActiveUserId, this.lastActiveUrl,"autoForecast",BiDashboardConstants.biDashboard];
  certificationsKeys = CERTIFICATIONS_LIST_SESSION_KEYS;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private userClient: UserClient
  ) {
    if (this.getUser()) {
      this.loggedIn.next(true);
    } else {
      this.loggedIn.next(false);
    }
  }

  setLoginData(response: SsoToken, skipLoginSet: boolean = false): void {
    sessionStorage.setItem(this.TOKEN, response.access_token);
    // Never overwrite a real refresh token with null/undefined — the API
    // elevation step has historically omitted refresh_token, and sessionStorage
    // stringifies those to "null"/"undefined", which then fails every refresh
    // with OpenIddict invalid_grant.
    if (response.refresh_token) {
      sessionStorage.setItem(this.REFRESH_TOKEN, response.refresh_token);
    }

    if (!skipLoginSet) {
      this.loggedIn.next(true);
    }
  }

  isSessionActive() {
    return sessionStorage.getItem("isSessionActive");
  }

  setSessionActive() {
    sessionStorage.setItem("isSessionActive", "true");
  }

  getAndRemoveLoginData(): any {
    const token = sessionStorage.getItem(this.TOKEN);
    const refreshToken = sessionStorage.getItem(this.REFRESH_TOKEN);
    const user = sessionStorage.getItem("user");
    sessionStorage.removeItem(this.TOKEN);
    sessionStorage.removeItem(this.REFRESH_TOKEN);
    sessionStorage.removeItem("user");
    return { token, refreshToken, user };
  }

  resetLoginData(token: string, refreshToken: string, user: string): void {
    sessionStorage.setItem(this.TOKEN, token);
    sessionStorage.setItem(this.REFRESH_TOKEN, refreshToken);
    sessionStorage.setItem("user", user);
    if (sessionStorage.getItem("user")) {
      this.isUserPhysician.next(
        JSON.parse(sessionStorage.getItem("user")!)?.npi ? true : false
      );
    }
  }

  setLoggedIn() {
    this.loggedIn.next(true);
  }

  setRefreshTokenData(response: SsoToken) {
    sessionStorage.setItem(this.TOKEN, response.access_token);
    if (response.refresh_token) {
      sessionStorage.setItem(this.REFRESH_TOKEN, response.refresh_token);
    }
    this.loggedIn.next(true);
  }

  setOrgCode(orgCode: string) {
    sessionStorage.setItem("orgCode", orgCode);
  }

  getOrgCode(): string | null {
    return sessionStorage.getItem("orgCode");
  }

  getAccessToken(): string | null {
    return sessionStorage.getItem(this.TOKEN);
  }

  getRefreshToken(): string | null {
    return sessionStorage.getItem(this.REFRESH_TOKEN);
  }

  clearUserSession() {
    sessionStorage.clear();
    localStorage.clear();
  }

  clearUserData(){
    const keys = Object.keys(sessionStorage);
    keys.forEach((key) => {
      if (!this.sessionStateKeys.includes(key)) {
        sessionStorage.removeItem(key);
      }
    });     
  }

  clearSessionState(){
    const keys = Object.keys(sessionStorage);
    keys.forEach((key) => {
      if (this.sessionStateKeys.includes(key)) {
        sessionStorage.removeItem(key);
      }
    });     
  }

  setSessionState(){
    sessionStorage.setItem(this.lastActiveUserId,this.getUserId()??"");
    sessionStorage.setItem(this.lastActiveUrl,this.router.url);
  }

  getLastActiveUrl(){
    return sessionStorage.getItem(this.lastActiveUrl);
  }

  getLastActiveUserId(){
    return sessionStorage.getItem(this.lastActiveUserId);
  }

  getUser(): string | null {
    return sessionStorage.getItem("user");
  }

  getUserId(): string | null {
    return JSON.parse(this.getUser()!)?.id;
  }
  getdisciplineIds(): any[] {
    return JSON.parse(this.getUser()!)?.disciplineids;
  }

  isAssistant(): boolean {
    const primaryGroup = [AllDisciplines.PT, AllDisciplines.OT, AllDisciplines.ST];
    const assistanceGroup = [AllDisciplines.PTA, AllDisciplines.COTA, AllDisciplines.STA, AllDisciplines.CFY];
  
    const userDisciplines = this.getdisciplineIds(); 
  
    // If the user has a primary discipline, they are NOT an assistant
    if (userDisciplines?.some((id: any) => primaryGroup.includes(id))) {
      return false;
    }
  
    // If the user has at least one assistant discipline and no primary, they are an assistant
    return userDisciplines?.some((id: any) => assistanceGroup.includes(id));
  }
  
  setUser(user: User) {
    sessionStorage.setItem("user", JSON.stringify(user));
    if (sessionStorage.getItem("user")) {
      this.isUserPhysician.next(
        JSON.parse(sessionStorage.getItem("user")!)?.npi ? true : false
      );
    }
  }

  setFacilities(facilities: SessionFacility[]) {
    sessionStorage.setItem("facilities", JSON.stringify(facilities));
  }

  getFacilities(): SessionFacility[] {
    const facilities = sessionStorage.getItem("facilities");
    if (facilities) {
      return JSON.parse(facilities);
    }
    return [];
  }

  setSelectedFacilityId(id: string) {
    sessionStorage.setItem("selectedFacilityId", id);
    this.isSelectedFacility.next(id);
  }

  getSelectedFacilityId(): string | null {
    return sessionStorage.getItem("selectedFacilityId");
  }

  setPasswordResetId(id: string) {
    sessionStorage.setItem("passwordResetId", id);
  }

  getPasswordResetId(): string | null {
    return sessionStorage.getItem("passwordResetId");
  }

  getBiDashboard() {
    if (sessionStorage.getItem(BiDashboardConstants.biDashboard) !== null) {
      return JSON.parse(sessionStorage.getItem(BiDashboardConstants.biDashboard)!) as BiDashboardData;
    }
    return null;
  }

  setBiDashboard(data: BiDashboardData) {
    if (data) {
      sessionStorage.setItem(BiDashboardConstants.biDashboard, JSON.stringify(data));
    }
  }

  setSecurityFeatureData(data: any) {
    if (data && data.length > 0) {
      sessionStorage.setItem(
        "securityFeatureData",
        this.encodeBase64(JSON.stringify(data))
      );
    }
    this.isSelectedFacility.next("" + this.getSelectedFacilityId());
    this.securityGroupChangedSubject.next(true);
  }

  getSecurityFeatureData() {
    if (sessionStorage.getItem("securityFeatureData")) {
      return JSON.parse(
        this.decodeBase64(sessionStorage.getItem("securityFeatureData")!)
      );
    }
  }

  getTranscribeTokens() {
    if (
      sessionStorage.getItem(UserSession.transcribeTokenData) !== null &&
      sessionStorage.getItem(UserSession.transcribeTokenData) !== ""
    ) {
      return JSON.parse(
        sessionStorage.getItem(UserSession.transcribeTokenData)!
      ) as TranscribeToken;
    }
    return null;
  }

  setTranscribeTokens(data: TranscribeToken) {
    if (data) {
      sessionStorage.setItem(
        UserSession.transcribeTokenData,
        JSON.stringify(data)
      );
    }
  }

  private encodeBase64(data: string) {
    return CryptoJS.AES.encrypt(
      data,
      environment.sessionEncryptionKey
    ).toString();
  }
  private decodeBase64(data: string) {
    return CryptoJS.AES.decrypt(
      data,
      environment.sessionEncryptionKey
    ).toString(CryptoJS.enc.Utf8);
  }

  lockUserSession() {
    this.setLockUser(true);
    this.dialog.closeAll();
  }

  setLockUser(lockUser: boolean) {
    sessionStorage.setItem("lockUser", JSON.stringify(lockUser));
  }

  isUserLocked(): boolean {
    return JSON.parse(sessionStorage.getItem("lockUser") || "false");
  }

  getSelectedCorporationId(): string | null {
    return sessionStorage.getItem("selectedCorporationId");
  }

  setSelectedCorporationId(corpId: string) {
    sessionStorage.setItem("selectedCorporationId", corpId);
  }

  setClientBaseListPageSizeAndNumber(pageSize: number, pageNumber: number){
    sessionStorage.setItem(this.certificationsKeys.PAGE_SIZE , pageSize.toString());
    sessionStorage.setItem(this.certificationsKeys.PAGE_NUMBER, pageNumber.toString());
  }

  getClientBaseListPageSizeAndNumber(){
    return  {
      pageSize: sessionStorage.getItem(this.certificationsKeys.PAGE_SIZE),
      pageNumber: sessionStorage.getItem(this.certificationsKeys.PAGE_NUMBER)
    }
  }

  setSsoUserId(id: string): void {
    sessionStorage.setItem("ssoUserId", id);
  }

  getSsoUserId(): string | null {
    return sessionStorage.getItem("ssoUserId");
  }
}
